import { useState } from "react";
import { Alert, CircularProgress, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import type {
  HolderConfiguration,
  SettingChange,
} from "@thalamos/haven-common";

import { ConfirmChangesDialog } from "./ConfirmChangesDialog.js";
import { SettingsEditor } from "./SettingsEditor.js";
import { UnsavedChangesBar } from "./UnsavedChangesBar.js";
import { useUnsavedSettings } from "./unsavedSettings.js";

export type HolderSettingsPanelProps = {
  holderName: string;
  configuration: HolderConfiguration | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  /** Which organisations saving applies to. */
  appliesTo: string;
  showChain?: boolean;
  /** Whether the changes were saved. */
  onSave: (changes: readonly SettingChange[]) => Promisable<boolean>;
};

/**
 * One holder's settings, with changes left unsaved on the page until they are
 * saved together. Nothing applies until the administrator has seen what changes and
 * which organisations it applies to, and confirmed it.
 */
export const HolderSettingsPanel = ({
  holderName,
  configuration,
  loading,
  saving,
  error,
  appliesTo,
  showChain = false,
  onSave,
}: HolderSettingsPanelProps) => {
  const { t } = useTranslation();
  const { unsaved, changes, changeSetting, discard } = useUnsavedSettings();
  const [confirming, setConfirming] = useState(false);

  if (configuration === null) {
    return loading ? (
      <CircularProgress size={20} />
    ) : (
      <Alert severity="error">
        {error ?? t("admin.configuration.settings.loadFailed")}
      </Alert>
    );
  }

  const confirm = async () => {
    if (await onSave(changes)) {
      discard();
      setConfirming(false);
    }
  };

  return (
    <Stack spacing={2}>
      <SettingsEditor
        configuration={configuration}
        unsaved={unsaved}
        saving={saving}
        error={confirming ? null : error}
        showChain={showChain}
        onChangeSetting={changeSetting}
      />

      {changes.length > 0 && (
        <UnsavedChangesBar
          summary={t("admin.configuration.settings.unsaved.count", {
            count: changes.length,
          })}
          detail={appliesTo}
          disabled={saving}
          onDiscard={discard}
          onSave={() => {
            setConfirming(true);
          }}
        />
      )}

      <ConfirmChangesDialog
        open={confirming}
        title={t("admin.configuration.settings.unsaved.confirmTitle", {
          count: changes.length,
          name: holderName,
        })}
        confirmLabel={t("admin.configuration.settings.unsaved.confirm", {
          count: changes.length,
        })}
        saving={saving}
        error={confirming ? error : null}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={confirm}
      >
        <Typography variant="body2">{appliesTo}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t("admin.configuration.settings.unsaved.noAccessChange")}
        </Typography>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2" component="h3">
            {t("admin.configuration.settings.unsaved.changes")}
          </Typography>
          <Stack component="ul" sx={{ m: 0, pl: 3 }}>
            {changes.map((change) => (
              <Typography key={change.key} component="li" variant="body2">
                {t(`admin.configuration.settings.keys.${change.key}`)}
              </Typography>
            ))}
          </Stack>
        </Stack>
      </ConfirmChangesDialog>
    </Stack>
  );
};
